import collapseDeleted from "@ext/git/core/Diff/logic/collapseDeleted";
import { useDiffStyles } from "@ext/git/core/Diff/logic/hooks/useDiffStyles";
import t from "@ext/localization/locale/translate";
import { getSimpleExtensions } from "@ext/markdown/core/edit/logic/getExtensions";
import Comment from "@ext/markdown/elements/comment/edit/model/comment";
import type { AppSettingsFormData } from "@ext/settings/logic/formSchema";
import { DiffExtension } from "@gaurussel/tiptap-diff-utility";
import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import { type JSONContent, useEditor } from "@tiptap/react";
import { useEffect, useMemo, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";

const paragraph = (text: string): JSONContent => ({
	type: "paragraph",
	content: [{ type: "text", text }],
});

const doc = (...texts: string[]): JSONContent => ({ type: "doc", content: texts.map(paragraph) });

const previewBaseline = () =>
	doc(
		t("app-settings.content-compare.preview.before.typo"),
		t("app-settings.content-compare.preview.before.reworded"),
		t("app-settings.content-compare.preview.before.survivors"),
	);

const previewContent = () =>
	doc(
		t("app-settings.content-compare.preview.after.typo"),
		t("app-settings.content-compare.preview.after.reworded"),
		t("app-settings.content-compare.preview.after.survivors"),
	);

const PreviewCollapse = (getThreshold: () => number) =>
	Extension.create({
		name: "previewCollapse",
		addProseMirrorPlugins() {
			return [collapseDeleted(() => this.editor.storage.diff?.changes, getThreshold)];
		},
	});

export const useComparePreviewEditor = (form: UseFormReturn<AppSettingsFormData>) => {
	const sensitivity = form.watch("contentCompare.sensitivity");
	const minSimilarity = form.watch("contentCompare.minSimilarity");
	const debounce = form.watch("contentCompare.debounce");
	const minMatchLength = form.watch("contentCompare.minMatchLength");
	const stopWordMinBlocks = form.watch("contentCompare.stopWordMinBlocks");
	const cutDownMinWords = form.watch("contentCompare.cutDownMinWords");
	const cutDownMinKept = form.watch("contentCompare.cutDownMinKept");
	const collapseThreshold = form.watch("contentCompare.collapseThreshold");

	const thresholdRef = useRef(collapseThreshold);
	thresholdRef.current = collapseThreshold;

	useDiffStyles();
	const baseline = useMemo(() => previewBaseline(), []);
	const content = useMemo(() => previewContent(), []);

	const editor = useEditor({
		editable: false,
		extensions: [
			Document,
			Comment,
			...getSimpleExtensions(),
			DiffExtension.configure({
				baseline,
				minSimilarity: minSimilarity,
				debounceMs: debounce,
				minMatchLength: minMatchLength,
				sensitivity: sensitivity,
				stopWordMinBlocks,
				cutDownMinWords,
				cutDownMinKept,
			}),
			PreviewCollapse(() => thresholdRef.current),
		],
		content,
	});

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		editor.commands.setDiffOptions({
			enabled: true,
			baseline,
			minSimilarity: minSimilarity,
			debounceMs: debounce,
			minMatchLength: minMatchLength,
			sensitivity: sensitivity,
			stopWordMinBlocks,
			cutDownMinWords,
			cutDownMinKept,
		});
	}, [
		baseline,
		minSimilarity,
		debounce,
		minMatchLength,
		sensitivity,
		stopWordMinBlocks,
		cutDownMinWords,
		cutDownMinKept,
		editor,
	]);

	// The plugin rereads the threshold only on the next view update; this transaction triggers it.
	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		editor.view.dispatch(editor.state.tr.setMeta("collapseThreshold", collapseThreshold));
	}, [collapseThreshold, editor]);

	return editor;
};
